import { createRouter, createWebHistory } from "vue-router";
import Home from "../views/Home.vue";
import Login from "../views/Login.vue";
import AuthCallback from "../views/AuthCallback.vue";
import RaceSelection from "../views/RaceSelection.vue";
import { useAuthStore } from "../store/auth";
import { useUiStore } from "../store/ui";
import GameLayout from "../views/GameLayout.vue";
import GameCity from "../views/GameCity.vue";
import GameHero from "../views/GameHero.vue";
import GameCards from "../views/GameCards.vue";
import GameQuest from "../views/GameQuest.vue";
import GameTrade from "../views/GameTrade.vue";
import WorldMap from "../views/WorldMap.vue";
import GameHome from "../views/GameHome.vue";
import GameBattle from "../views/GameBattle.vue";
import GameGuild from "../views/GameGuild.vue";

const routes = [
  // Marketing pages
  { path: "/", name: "Home", component: Home },
  { path: "/login", name: "Login", component: Login },
  { path: "/auth/callback", name: "AuthCallback", component: AuthCallback },

  // Game pages, nested under the new layout
  {
    path: "/game",
    component: GameLayout,
    meta: { requiresAuth: true, isGame: true },
    children: [
      { path: "", redirect: "/game/home" },
      { path: "home", name: "GameHome", component: GameHome },
      { path: "city", name: "GameCity", component: GameCity },
      { path: "hero", name: "GameHero", component: GameHero },
      { path: "cards", name: "GameCards", component: GameCards },
      { path: "quest", name: "GameQuest", component: GameQuest },
      { path: "battle", name: "GameBattle", component: GameBattle },
      { path: "world", name: "GameWorld", component: WorldMap },
      { path: "guild", name: "GameGuild", component: GameGuild },
      { path: "trade", name: "GameTrade", component: GameTrade },
    ],
  },
  // Race selection is now a top-level route that requires auth
  {
    path: "/race-selection",
    name: "RaceSelection",
    component: RaceSelection,
    meta: { requiresAuth: true },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // On initial app load, try to fetch the user to restore a session
  if (!authStore.isInitialized) {
    await authStore.fetchUser();
  }

  const isAuthenticated = !!authStore.user;
  const hasRace = !!authStore.user?.race;

  // If trying to access a game page but not race selection, and the user has no race, force them to choose.
  if (to.meta.isGame && isAuthenticated && !hasRace) {
    return next({ name: "RaceSelection" });
  }

  // If a logged-in user with a race tries to go to the race selection page, send them to the game home.
  if (to.name === "RaceSelection" && isAuthenticated && hasRace) {
    return next({ path: "/game/home" });
  }

  // If trying to access a route that requires auth and the user is not authenticated, send them to login.
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: "Login" });
  }

  // If a logged-in user tries to visit the marketing home or login page, redirect them into the game.
  if ((to.name === "Home" || to.name === "Login") && isAuthenticated) {
    return next({ path: "/game/home" });
  }

  // Otherwise, allow the navigation.
  return next();
});

router.afterEach(() => {
  const uiStore = useUiStore();
  uiStore.setInfoDrawerOpen(false);
});

export default router;
